import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { getEmpresaByCodigo } from './empresasConfig';

const getLogoPath = (selectedCard) => getEmpresaByCodigo(selectedCard).logoPDF;
const getEmpresaColor = (selectedCard) => getEmpresaByCodigo(selectedCard).color;
const getEmpresaRUT = (selectedCard) => getEmpresaByCodigo(selectedCard).rutFormatted;
const getEmpresaName = (selectedCard) => getEmpresaByCodigo(selectedCard).nombre;

// Cargar logo como imagen
const cargarLogo = (selectedCard) => {
    return new Promise((resolve) => {
        const logoPath = getLogoPath(selectedCard);
        const logoImg = new Image();
        logoImg.onload = () => resolve(logoImg);
        logoImg.onerror = () => {
            console.warn('No se pudo cargar el logo');
            resolve(null);
        };
        logoImg.src = logoPath;
    });
};

// ==========================================
// EXPORTAR A PDF
// ==========================================
export const exportarAPDF = async (datos, configuracion) => {
    try {
        const {
            titulo,
            nombreArchivo,
            selectedCard,
            empresa,
            columnas,
            filas,
            nombreTrabajador,
            mesesSeleccionados,
            anioSeleccionado
        } = configuracion;

        const doc = new jsPDF();
        const primaryColor = getEmpresaColor(selectedCard);

        // Cargar logo
        const logoData = await cargarLogo(selectedCard);

        // Agregar logo si se cargó exitosamente
        if (logoData) {
            doc.addImage(logoData, 'PNG', 15, 10, 45, 15);
        }

        // Encabezado principal
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(String(empresa || getEmpresaName(selectedCard)), 15, 32);

        // RUT de la empresa
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text('RUT: ' + String(getEmpresaRUT(selectedCard)), 15, 37);

        // Fecha de generación
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text('Fecha: ' + String(new Date().toLocaleDateString('es-CL')), 140, 20);

        // Título del documento
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(String(titulo), 105, 48, { align: 'center' });

        // Información adicional
        let yPos = 58;
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);

        if (nombreTrabajador) {
            doc.text(`Trabajador: ${String(nombreTrabajador)}`, 15, yPos);
            yPos += 7;
        }

        // Información de período
        if (mesesSeleccionados && mesesSeleccionados.length > 0) {
            const nombresMeses = [
                'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
            ];

            if (mesesSeleccionados.includes(0)) {
                doc.text(`Período: Todos los registros - Año ${String(anioSeleccionado)}`, 15, yPos);
            } else {
                const mesesTexto = mesesSeleccionados
                    .sort((a, b) => a - b)
                    .map(m => nombresMeses[m - 1])
                    .join(', ');
                doc.text(`Período: ${mesesTexto} de ${String(anioSeleccionado)}`, 15, yPos);
            }
            yPos += 7;
        }

        doc.text(`Total de registros: ${String(filas.length)}`, 15, yPos);
        yPos += 10;

        // Convertir todos los valores de las filas a strings
        const filasFormateadas = filas.map(fila =>
            fila.map(valor => {
                if (valor === null || valor === undefined) return '';
                return String(valor);
            })
        );

        // Tabla de datos
        doc.autoTable({
            startY: yPos,
            head: [columnas],
            body: filasFormateadas,
            theme: 'grid',
            headStyles: {
                fillColor: primaryColor,
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                fontSize: 9,
                cellPadding: 3
            },
            bodyStyles: {
                fontSize: 8,
                cellPadding: 2,
                lineWidth: 0.1
            },
            margin: { left: 15, right: 15 },
            styles: {
                lineColor: [200, 200, 200],
                lineWidth: 0.1
            }
        });

        // Pie de página
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(120, 120, 120);
        const pageHeight = doc.internal.pageSize.height;
        doc.text(String(empresa || getEmpresaName(selectedCard)), 15, pageHeight - 8);
        doc.text('Generado: ' + String(new Date().toLocaleString('es-CL')), 130, pageHeight - 8);

        // Guardar el PDF
        doc.save(nombreArchivo);

        Swal.fire({
            icon: 'success',
            title: 'Exportación exitosa',
            text: 'El archivo PDF se ha generado correctamente.',
            confirmButtonColor: '#3085d6'
        });

    } catch (error) {
        console.error('Error al generar PDF:', error);
        Swal.fire({
            icon: 'error',
            title: 'Error al generar PDF',
            text: 'No se pudo generar el PDF. Por favor, inténtelo nuevamente.',
            confirmButtonColor: '#d33'
        });
    }
};

// ==========================================
// EXPORTAR A EXCEL
// ==========================================
export const exportarAExcel = (datos, configuracion) => {
    try {
        const {
            nombreArchivo,
            columnas,
            filas,
            nombreTrabajador,
            mesesSeleccionados,
            anioSeleccionado,
            hojasAdicionales = [] // [{ nombre, filas, anchos }] que se agregan después de 'Datos'
        } = configuracion;

        // Crear un nuevo libro de trabajo
        const wb = XLSX.utils.book_new();

        // Preparar datos con encabezado
        const datosExcel = [columnas, ...filas];

        // Crear hoja de trabajo
        const ws = XLSX.utils.aoa_to_sheet(datosExcel);

        // Ajustar ancho de columnas
        const columnWidths = columnas.map(() => ({ wch: 20 }));
        ws['!cols'] = columnWidths;

        // Agregar la hoja al libro
        XLSX.utils.book_append_sheet(wb, ws, 'Datos');

        hojasAdicionales.forEach(({ nombre, filas: filasHoja, anchos }) => {
            const wsExtra = XLSX.utils.aoa_to_sheet(filasHoja);
            if (anchos) wsExtra['!cols'] = anchos.map(wch => ({ wch }));
            XLSX.utils.book_append_sheet(wb, wsExtra, nombre);
        });

        // Crear hoja de información
        const infoData = [
            ['INFORMACIÓN DEL REPORTE'],
            [''],
            ['Trabajador:', nombreTrabajador || 'N/A'],
            ['Año:', anioSeleccionado],
            ['Total de registros:', filas.length],
            ['Fecha de generación:', new Date().toLocaleString('es-CL')]
        ];

        if (mesesSeleccionados && mesesSeleccionados.length > 0) {
            const nombresMeses = [
                'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
            ];

            if (mesesSeleccionados.includes(0)) {
                infoData.splice(3, 0, ['Período:', 'Todos los registros']);
            } else {
                const mesesTexto = mesesSeleccionados
                    .sort((a, b) => a - b)
                    .map(m => nombresMeses[m - 1])
                    .join(', ');
                infoData.splice(3, 0, ['Período:', mesesTexto]);
            }
        }

        const wsInfo = XLSX.utils.aoa_to_sheet(infoData);
        wsInfo['!cols'] = [{ wch: 25 }, { wch: 40 }];
        XLSX.utils.book_append_sheet(wb, wsInfo, 'Información');

        // Descargar el archivo
        XLSX.writeFile(wb, nombreArchivo);

        Swal.fire({
            icon: 'success',
            title: 'Exportación exitosa',
            text: 'El archivo Excel se ha generado correctamente.',
            confirmButtonColor: '#3085d6'
        });

    } catch (error) {
        console.error('Error al generar Excel:', error);
        Swal.fire({
            icon: 'error',
            title: 'Error al generar Excel',
            text: 'No se pudo generar el archivo Excel. Por favor, inténtelo nuevamente.',
            confirmButtonColor: '#d33'
        });
    }
};

// ==========================================
// MODAL DE EXPORTACIÓN CON SWEETALERT2
// ==========================================
export const mostrarModalExportacion = async (datos, anioActual, callback, trabajadores = null, verTodosTrabajadores = false) => {
    const meses = [
        { value: 1, label: 'Enero' },
        { value: 2, label: 'Febrero' },
        { value: 3, label: 'Marzo' },
        { value: 4, label: 'Abril' },
        { value: 5, label: 'Mayo' },
        { value: 6, label: 'Junio' },
        { value: 7, label: 'Julio' },
        { value: 8, label: 'Agosto' },
        { value: 9, label: 'Septiembre' },
        { value: 10, label: 'Octubre' },
        { value: 11, label: 'Noviembre' },
        { value: 12, label: 'Diciembre' }
    ];

    const { value: formValues } = await Swal.fire({
        title: 'Exportar Datos',
        html: `
            <div style="text-align: left;">
                <div style="margin-bottom: 20px;">
                    <label style="display: block; margin-bottom: 8px; font-weight: bold; color: #333;">Formato de exportación:</label>
                    <select id="formato-exportacion" style="width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 5px; font-size: 14px; box-sizing: border-box; margin: 0;">
                        <option value="pdf">📄 PDF</option>
                        <option value="excel">📊 Excel</option>
                    </select>
                </div>
                
                ${verTodosTrabajadores && trabajadores ? `
                <div style="margin-bottom: 20px;">
                    <label style="display: block; margin-bottom: 8px; font-weight: bold; color: #333;">Seleccionar trabajadores:</label>
                    <div style="max-height: 280px; overflow-y: auto; overflow-x: hidden; border: 1px solid #d1d5db; padding: 12px; border-radius: 8px; background-color: #f9fafb; box-sizing: border-box;">
                        <div style="background-color: #e0f2fe; padding: 10px; border-radius: 5px; margin-bottom: 12px; border-left: 4px solid #0284c7;">
                            <label style="display: flex; align-items: center; cursor: pointer; margin: 0;">
                                <input type="checkbox" value="0" id="trabajador-todos" style="margin-right: 10px; width: 18px; height: 18px; cursor: pointer;">
                                <strong style="color: #0c4a6e; font-size: 15px;">TODOS LOS TRABAJADORES</strong>
                            </label>
                        </div>
                        <div style="display: flex; flex-direction: column; gap: 8px;">
                            ${trabajadores.map(trabajador => `
                                <label style="display: flex; align-items: center; padding: 8px 10px; cursor: pointer; border-radius: 5px; transition: all 0.2s; background-color: white; border: 1px solid #e5e7eb;" class="trabajador-checkbox">
                                    <input type="checkbox" value="${trabajador.ID_Trabajador}" class="trabajador-individual" style="margin-right: 8px; width: 16px; height: 16px; cursor: pointer;">
                                    <span style="font-size: 14px; color: #374151;">${trabajador.NombreTrabajador || (trabajador.Nombres + ' ' + trabajador.Apellidos)}</span>
                                </label>
                            `).join('')}
                        </div>
                    </div>
                </div>
                ` : ''}
                
                <div style="margin-bottom: 20px;">
                    <label style="display: block; margin-bottom: 8px; font-weight: bold; color: #333;">Seleccionar meses:</label>
                    <div style="max-height: 280px; overflow-y: auto; overflow-x: hidden; border: 1px solid #d1d5db; padding: 12px; border-radius: 8px; background-color: #f9fafb; box-sizing: border-box;">
                        <div style="background-color: #e0f2fe; padding: 10px; border-radius: 5px; margin-bottom: 12px; border-left: 4px solid #0284c7;">
                            <label style="display: flex; align-items: center; cursor: pointer; margin: 0;">
                                <input type="checkbox" value="0" id="mes-total" style="margin-right: 10px; width: 18px; height: 18px; cursor: pointer;">
                                <strong style="color: #0c4a6e; font-size: 15px;">TODOS LOS MESES</strong>
                            </label>
                        </div>
                        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
                            ${meses.map(mes => `
                                <label style="display: flex; align-items: center; padding: 8px 10px; cursor: pointer; border-radius: 5px; transition: all 0.2s; background-color: white; border: 1px solid #e5e7eb;" class="mes-checkbox">
                                    <input type="checkbox" value="${mes.value}" class="mes-individual" style="margin-right: 8px; width: 16px; height: 16px; cursor: pointer;">
                                    <span style="font-size: 14px; color: #374151;">${mes.label}</span>
                                </label>
                            `).join('')}
                        </div>
                    </div>
                </div>
                
                <div style="margin-bottom: 15px;">
                    <label style="display: block; margin-bottom: 8px; font-weight: bold; color: #333;">Año:</label>
                    <select id="anio-exportacion" style="width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 5px; font-size: 14px; box-sizing: border-box; margin: 0;">
                        ${[...Array(11)].map((_, i) => {
            const year = 2020 + i;
            return `<option value="${year}" ${year === anioActual ? 'selected' : ''}>${year}</option>`;
        }).join('')}
                    </select>
                </div>
            </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Exportar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        width: '550px',
        customClass: {
            htmlContainer: 'swal2-html-container-custom'
        },
        didOpen: () => {
            // Agregar estilos hover a las etiquetas de meses
            const labelsMeses = document.querySelectorAll('.mes-checkbox');
            labelsMeses.forEach(label => {
                label.addEventListener('mouseenter', () => {
                    label.style.backgroundColor = '#f3f4f6';
                    label.style.borderColor = '#9ca3af';
                    label.style.transform = 'translateX(2px)';
                });
                label.addEventListener('mouseleave', () => {
                    label.style.backgroundColor = 'white';
                    label.style.borderColor = '#e5e7eb';
                    label.style.transform = 'translateX(0)';
                });
            });

            // Agregar estilos hover a las etiquetas de trabajadores
            const labelsTrabajadores = document.querySelectorAll('.trabajador-checkbox');
            labelsTrabajadores.forEach(label => {
                label.addEventListener('mouseenter', () => {
                    label.style.backgroundColor = '#f3f4f6';
                    label.style.borderColor = '#9ca3af';
                    label.style.transform = 'translateX(2px)';
                });
                label.addEventListener('mouseleave', () => {
                    label.style.backgroundColor = 'white';
                    label.style.borderColor = '#e5e7eb';
                    label.style.transform = 'translateX(0)';
                });
            });

            // Manejar la selección de trabajadores (si está en modo todos los trabajadores)
            if (verTodosTrabajadores && trabajadores) {
                const checkboxTodosTrabajadores = document.getElementById('trabajador-todos');
                const checkboxesTrabajadoresIndiv = document.querySelectorAll('.trabajador-individual');

                checkboxTodosTrabajadores.addEventListener('change', (e) => {
                    if (e.target.checked) {
                        checkboxesTrabajadoresIndiv.forEach(cb => {
                            cb.checked = false;
                            cb.disabled = true;
                            cb.parentElement.style.opacity = '0.5';
                            cb.parentElement.style.cursor = 'not-allowed';
                        });
                    } else {
                        checkboxesTrabajadoresIndiv.forEach(cb => {
                            cb.disabled = false;
                            cb.parentElement.style.opacity = '1';
                            cb.parentElement.style.cursor = 'pointer';
                        });
                    }
                });

                checkboxesTrabajadoresIndiv.forEach(cb => {
                    cb.addEventListener('change', () => {
                        const algunoMarcado = Array.from(checkboxesTrabajadoresIndiv).some(c => c.checked);
                        if (algunoMarcado) {
                            checkboxTodosTrabajadores.checked = false;
                            checkboxTodosTrabajadores.disabled = true;
                            checkboxTodosTrabajadores.parentElement.parentElement.style.opacity = '0.6';
                        } else {
                            checkboxTodosTrabajadores.disabled = false;
                            checkboxTodosTrabajadores.parentElement.parentElement.style.opacity = '1';
                        }
                    });
                });
            }

            // Manejar la selección de "TODOS LOS MESES"
            const checkboxTotal = document.getElementById('mes-total');
            const checkboxesIndividuales = document.querySelectorAll('.mes-individual');

            checkboxTotal.addEventListener('change', (e) => {
                if (e.target.checked) {
                    checkboxesIndividuales.forEach(cb => {
                        cb.checked = false;
                        cb.disabled = true;
                        cb.parentElement.style.opacity = '0.5';
                        cb.parentElement.style.cursor = 'not-allowed';
                    });
                } else {
                    checkboxesIndividuales.forEach(cb => {
                        cb.disabled = false;
                        cb.parentElement.style.opacity = '1';
                        cb.parentElement.style.cursor = 'pointer';
                    });
                }
            });

            checkboxesIndividuales.forEach(cb => {
                cb.addEventListener('change', () => {
                    const algunoMarcado = Array.from(checkboxesIndividuales).some(c => c.checked);
                    if (algunoMarcado) {
                        checkboxTotal.checked = false;
                        checkboxTotal.disabled = true;
                        checkboxTotal.parentElement.parentElement.style.opacity = '0.6';
                    } else {
                        checkboxTotal.disabled = false;
                        checkboxTotal.parentElement.parentElement.style.opacity = '1';
                    }
                });
            });
        },
        preConfirm: () => {
            const formato = document.getElementById('formato-exportacion').value;
            const anio = parseInt(document.getElementById('anio-exportacion').value);
            const checkboxTotal = document.getElementById('mes-total');
            const checkboxes = document.querySelectorAll('.mes-individual');

            let mesesSeleccionados = [];

            if (checkboxTotal.checked) {
                mesesSeleccionados = [0]; // 0 representa TODOS
            } else {
                checkboxes.forEach(checkbox => {
                    if (checkbox.checked) {
                        mesesSeleccionados.push(parseInt(checkbox.value));
                    }
                });
            }

            if (mesesSeleccionados.length === 0) {
                Swal.showValidationMessage('Por favor, seleccione al menos un mes');
                return false;
            }

            // Capturar trabajadores seleccionados si estamos en modo todos los trabajadores
            let trabajadoresSeleccionados = null;
            if (verTodosTrabajadores && trabajadores) {
                const checkboxTodosTrabajadores = document.getElementById('trabajador-todos');
                const checkboxesTrabajadores = document.querySelectorAll('.trabajador-individual');

                if (checkboxTodosTrabajadores.checked) {
                    trabajadoresSeleccionados = [0]; // 0 representa TODOS
                } else {
                    trabajadoresSeleccionados = [];
                    checkboxesTrabajadores.forEach(checkbox => {
                        if (checkbox.checked) {
                            trabajadoresSeleccionados.push(parseInt(checkbox.value));
                        }
                    });
                }

                if (trabajadoresSeleccionados.length === 0) {
                    Swal.showValidationMessage('Por favor, seleccione al menos un trabajador');
                    return false;
                }
            }

            return {
                formato,
                mesesSeleccionados,
                anio,
                trabajadoresSeleccionados
            };
        }
    });

    if (formValues) {
        callback(formValues);
    }
};
